import { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "./Localization";
import locationDisabledIcon from "../assets/images/icons/location-disabled.svg";
import locationCheckingIcon from "../assets/images/icons/location-checking.svg";

const ALLOWED_LOCATION = { latitude: 46.358367, longitude: 25.804818 };
const ALLOWED_RADIUS_METERS = 1000;

type LocationState = "checking" | "allowed" | "outside" | "denied" | "unavailable";

export function distanceInMeters(latitude: number, longitude: number) {
  const earthRadius = 6371000;
  const toRadians = (degrees: number) => degrees * Math.PI / 180;
  const latitudeDelta = toRadians(ALLOWED_LOCATION.latitude - latitude);
  const longitudeDelta = toRadians(ALLOWED_LOCATION.longitude - longitude);
  const a = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(toRadians(latitude)) * Math.cos(toRadians(ALLOWED_LOCATION.latitude))
    * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function GeolocationGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<LocationState>("checking");

  const checkLocation = useCallback(() => {
    setState("checking");
    if (!("geolocation" in navigator)) {
      setState("unavailable");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const distance = distanceInMeters(coords.latitude, coords.longitude);
        setState(distance <= ALLOWED_RADIUS_METERS
          ? "allowed"
          : "outside");
      },
      (error) => setState(error.code === error.PERMISSION_DENIED ? "denied" : "unavailable"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }, []);

  useEffect(() => checkLocation(), [checkLocation]);

  if (state === "allowed") return <>{children}</>;

  return (
    <main className="location-gate" role={state === "checking" ? "status" : "alert"}>
      <div className="location-gate-card">
        <div className={`location-pin${state === "checking" ? " is-checking" : ""}`} aria-hidden="true">
          <img
            src={state === "checking" ? locationCheckingIcon : locationDisabledIcon}
            alt=""
          />
        </div>
        <p>{t(`geolocation.${state}`)}</p>
        {state !== "checking" && (
          <button type="button" className="outline-button" onClick={checkLocation}>
            {t("geolocation.retry")}
          </button>
        )}
      </div>
    </main>
  );
}
